import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { UserAPI } from '../apis/UserAPI';
import { constants } from '../constants';
import { defaultCountries, parseCountry } from 'react-international-phone';
import PhoneInput from './PhoneInput';
import { validatePhoneNumber } from '../utils/phoneValidation';
// Helper function to get email input end adornment
const getEmailEndAdornment = (currentUser, userIndex, updateUserField, clearEmailValidation) => {
    if (currentUser.isValidatingEmail) {
        return (_jsx(InputAdornment, { position: "end", children: _jsx(CircularProgress, { size: 20 }) }));
    }
    if (currentUser.email) {
        return (_jsx(InputAdornment, { position: "end", children: _jsx(CloseIcon, { sx: {
                    color: '#595959',
                    fontSize: 20,
                    cursor: 'pointer'
                }, onClick: () => {
                    updateUserField(userIndex, 'email', '');
                    clearEmailValidation(userIndex);
                } }) }));
    }
    return null;
};
// Helper function to clear email validation for a user
const clearEmailValidationHelper = (userIndex, setUsers) => {
    setUsers((prev) => prev.map((user, index) => index === userIndex
        ? {
            ...user,
            emailValidationError: null,
            namesFromAPI: false
        }
        : user));
};
const AddUserModal = ({ open, onClose, onSave, user, loading, saveError }) => {
    const [users, setUsers] = useState([
        {
            id: Date.now(),
            email: '',
            firstName: '',
            lastName: '',
            phoneNumber: '',
            selectedCountry: 'us',
            phoneNumberError: null,
            namesFromAPI: false,
            isValidatingEmail: false,
            emailValidationError: null
        }
    ]);
    const [validatedEmails, setValidatedEmails] = useState(new Map());
    // Helper to mark user as validating
    const setUserValidating = (userIndex, isValidating) => {
        setUsers((prev) => prev.map((user, index) => index === userIndex
            ? {
                ...user,
                isValidatingEmail: isValidating,
                emailValidationError: null
            }
            : user));
    };
    // Helper to get cached validation result
    const getCachedValidation = (email) => {
        if (validatedEmails.has(email.toLowerCase())) {
            const cachedUserInfo = validatedEmails.get(email.toLowerCase());
            return {
                isValid: true,
                userInfo: cachedUserInfo
            };
        }
        return null;
    };
    // Helper to cache validation result
    const cacheValidationResult = (email, userInfo) => {
        setValidatedEmails((prev) => {
            const newMap = new Map(prev);
            newMap.set(email.toLowerCase(), userInfo);
            return newMap;
        });
    };
    // Helper to process validation result
    const handleValidationResult = (validationResult, email) => {
        if (validationResult.isValid && validationResult.userInfo) {
            cacheValidationResult(email, validationResult.userInfo);
            return {
                isValid: true,
                userInfo: validationResult.userInfo
            };
        }
        return {
            isValid: false,
            error: validationResult.error ??
                constants.add_user_modal_email_validation_failed
        };
    };
    // Function to validate email using UserAPI
    const validateEmailWithAPI = useCallback(async (email, userIndex) => {
        try {
            setUserValidating(userIndex, true);
            const cachedResult = getCachedValidation(email);
            if (cachedResult) {
                return cachedResult;
            }
            const validationResult = await UserAPI.validateEmail(email);
            return handleValidationResult(validationResult, email);
        }
        catch (error) {
            console.error('Email validation error:', error);
            return {
                isValid: false,
                error: constants.add_user_modal_email_validate_failed
            };
        }
        finally {
            setUserValidating(userIndex, false);
        }
    }, [validatedEmails]);
    // Helper to parse phone data from raw JSON or string
    const parsePhoneData = (rawPhone) => {
        let phoneNumber = '';
        let selectedCountry = 'us';
        if (!rawPhone) {
            return { phoneNumber, selectedCountry };
        }
        try {
            const parsed = JSON.parse(rawPhone);
            if (parsed?.country && parsed?.number) {
                selectedCountry = parsed.country.toLowerCase();
                phoneNumber = parsed.number;
            }
            else if (parsed?.dialCode && parsed?.number) {
                phoneNumber = parsed.number;
                const found = defaultCountries.find((c) => parseCountry(c).dialCode === parsed.dialCode);
                selectedCountry = found ? parseCountry(found).iso2 : 'us';
            }
        }
        catch {
            // Legacy plain string – pass as-is, no country extraction
            phoneNumber = rawPhone;
        }
        return { phoneNumber, selectedCountry };
    };
    // Helper to create user object from existing user data
    const createUserFromData = (userData) => {
        const { phoneNumber, selectedCountry } = parsePhoneData(userData.phone || '');
        return {
            id: Date.now(),
            email: userData.email,
            firstName: userData.name.split(' ')[0] || '',
            lastName: userData.name.split(' ').slice(1).join(' ') || '',
            phoneNumber,
            selectedCountry,
            phoneNumberError: null,
            namesFromAPI: false,
            isValidatingEmail: false,
            emailValidationError: null
        };
    };
    // Helper to create empty user object
    const createEmptyUser = () => ({
        id: Date.now(),
        email: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        selectedCountry: 'us',
        phoneNumberError: null,
        namesFromAPI: false,
        isValidatingEmail: false,
        emailValidationError: null
    });
    useEffect(() => {
        setUsers([user ? createUserFromData(user) : createEmptyUser()]);
        if (open) {
            setValidatedEmails(new Map());
        }
    }, [user, open]);
    // Helper function to parse user name from API response
    const parseUserName = (fullName) => {
        let firstName = '';
        let lastName = '';
        if (fullName.includes(',')) {
            const nameParts = fullName
                .split(',')
                .map((part) => part.trim());
            lastName = nameParts[0] ?? '';
            firstName = nameParts[1] ?? '';
        }
        else {
            // Fallback: treat as "First Last" format
            const nameParts = fullName.split(' ');
            firstName = nameParts[0] ?? '';
            lastName = nameParts.slice(1).join(' ') || '';
        }
        return { firstName, lastName };
    };
    // Helper function to check if email should be validated via API
    const shouldValidateViaAPI = (userEmail) => {
        const isApprovedEmail = userEmail
            .toLowerCase()
            .endsWith(`@${process.env.REACT_APP_USER_LOOKUP_EMAIL_DOMAIN ?? ''}`);
        const isApiEnabled = JSON.parse(process.env.REACT_APP_IDP_USER_LOOKUP_API_ENABLED ?? 'false');
        return isApprovedEmail && isApiEnabled;
    };
    // Handle email validation and search
    const handleEmailSearch = async (userIndex) => {
        const userEmail = users[userIndex].email.trim();
        if (!userEmail) {
            setUsers((prev) => prev.map((user, index) => index === userIndex
                ? {
                    ...user,
                    emailValidationError: null
                }
                : user));
            return;
        }
        // Check if it looks like an email (contains @ and at least basic format)
        const isEmailLike = userEmail.includes('@') && userEmail.length > 3;
        if (isEmailLike) {
            // Only call validateEmail API for approved email addresses
            if (shouldValidateViaAPI(userEmail)) {
                // Call validateEmail API for approved email addresses
                const validationResult = await validateEmailWithAPI(userEmail, userIndex);
                if (validationResult.isValid && validationResult.userInfo) {
                    // Parse name from API response
                    const { firstName, lastName } = parseUserName(validationResult.userInfo.name ?? '');
                    // Auto-populate form fields with validated user data
                    setUsers((prev) => prev.map((user, index) => index === userIndex
                        ? {
                            ...user,
                            firstName,
                            lastName,
                            namesFromAPI: true,
                            emailValidationError: null
                        }
                        : user));
                }
                else {
                    setUsers((prev) => prev.map((user, index) => index === userIndex
                        ? {
                            ...user,
                            emailValidationError: validationResult.error ||
                                constants.add_user_modal_email_invalid,
                            namesFromAPI: false
                        }
                        : user));
                }
            }
            else {
                // For non-Deloitte emails, just clear any previous results and errors
                // Don't call API, allow manual entry
                setUsers((prev) => prev.map((user, index) => index === userIndex
                    ? {
                        ...user,
                        emailValidationError: null,
                        namesFromAPI: false
                    }
                    : user));
            }
        }
        else {
            // Clear search results when query is empty
            setUsers((prev) => prev.map((user, index) => index === userIndex
                ? {
                    ...user,
                    emailValidationError: null,
                    namesFromAPI: false
                }
                : user));
        }
    };
    // Helper function to get button text
    const getButtonText = () => {
        if (loading) {
            return user
                ? constants.configuration_modal_button_updating
                : constants.configuration_modal_button_adding;
        }
        return user
            ? constants.configuration_modal_button_update
            : constants.configuration_modal_button_add;
    };
    // Add a new user form
    const addUser = () => {
        setUsers((prev) => [
            ...prev,
            {
                id: Date.now(),
                email: '',
                firstName: '',
                lastName: '',
                phoneNumber: '',
                selectedCountry: 'us',
                phoneNumberError: null,
                namesFromAPI: false,
                isValidatingEmail: false,
                emailValidationError: null
            }
        ]);
    };
    // Remove a user form
    const removeUser = (userIndex) => {
        if (users.length > 1) {
            setUsers((prev) => prev.filter((_, index) => index !== userIndex));
        }
    };
    // Clear email validation for a user
    const clearEmailValidation = useCallback((userIndex) => {
        clearEmailValidationHelper(userIndex, setUsers);
    }, []);
    // Helper to update phone number and validate
    const handlePhoneValueChange = (newValue, userIndex) => {
        const error = validatePhoneNumber(newValue, users[userIndex].selectedCountry, false);
        setUsers((prev) => prev.map((u, idx) => idx === userIndex
            ? {
                ...u,
                phoneNumber: newValue,
                phoneNumberError: error
            }
            : u));
    };
    // Helper to update country and validate phone
    const handleCountryChange = (newCountry, userIndex) => {
        const error = users[userIndex].phoneNumber
            ? validatePhoneNumber(users[userIndex].phoneNumber, newCountry, false)
            : null;
        setUsers((prev) => prev.map((u, idx) => idx === userIndex
            ? {
                ...u,
                selectedCountry: newCountry,
                phoneNumberError: error
            }
            : u));
    };
    // Update user field
    const updateUserField = (userIndex, field, value) => {
        setUsers((prev) => prev.map((user, index) => index === userIndex
            ? {
                ...user,
                [field]: value,
                // Reset namesFromAPI if email is manually changed
                ...(field === 'email' && user.namesFromAPI
                    ? { namesFromAPI: false }
                    : {})
            }
            : user));
    };
    const handleSave = () => {
        const usersData = users.map((u) => {
            let phone = '';
            if (u.phoneNumber) {
                const countryData = defaultCountries.find((c) => c[1] === u.selectedCountry);
                const dialCode = countryData
                    ? parseCountry(countryData).dialCode
                    : '1';
                phone = `+${dialCode}${u.phoneNumber}`;
            }
            return {
                id: u.id.toString(),
                name: `${u.firstName} ${u.lastName}`.trim(),
                email: u.email,
                phone,
                role: '',
                avatarUrl: ''
            };
        });
        // Delegate to parent for both create and update
        onSave(usersData);
    };
    const canAdd = users.every((u) => u.email && u.firstName && u.lastName) &&
        users.every((u) => !u.phoneNumberError);
    return (_jsxs(Dialog, { open: open, onClose: onClose, maxWidth: "sm", fullWidth: true, sx: {
            '& .MuiDialog-paper': {
                borderRadius: 2,
                boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
                minHeight: 400
            }
        }, children: [_jsx(DialogTitle, { variant: "h6", "data-testid": "modal-title", sx: {
                    color: '#212121',
                    borderBottom: '1px solid #ebebeb',
                    fontFamily: 'Open Sans',
                    fontWeight: 600,
                    fontSize: 20,
                    px: 3,
                    py: 3
                }, children: user
                    ? constants.edit_user_modal_title
                    : constants.add_user_modal_title }), saveError && (_jsx(Alert, { severity: "error", sx: {
                    mx: 3,
                    mt: 2,
                    fontFamily: 'Open Sans'
                }, children: saveError })), _jsx(DialogContent, { sx: { p: '24px !important' }, children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 }, children: [users.map((currentUser, userIndex) => (_jsxs(Box, { sx: {
                                position: 'relative',
                                border: '1px solid #d9d9d9',
                                borderRadius: '8px',
                                p: 2,
                                pr: users.length > 1 ? 5 : 2, // Add extra padding on right when delete button is present
                                ...(userIndex > 0 && { mt: 3 })
                            }, children: [users.length > 1 && (_jsx(Box, { sx: {
                                        position: 'absolute',
                                        top: 12,
                                        right: 12,
                                        cursor: 'pointer',
                                        zIndex: 10,
                                        backgroundColor: 'white',
                                        borderRadius: '50%',
                                        width: 28,
                                        height: 28,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '&:hover': {
                                            backgroundColor: '#f5f5f5'
                                        }
                                    }, onClick: () => removeUser(userIndex), children: _jsx(DeleteIcon, { sx: { color: '#d32f2f', fontSize: 18 } }) })), _jsx(Box, { sx: { mb: 2, position: 'relative' }, children: _jsx(TextField, { fullWidth: true, size: "small", label: constants.add_user_modal_email, placeholder: constants.add_user_modal_email, required: true, disabled: !!user, value: currentUser.email, onChange: (e) => {
                                            const newEmail = e.target.value;
                                            updateUserField(userIndex, 'email', newEmail);
                                        }, onBlur: (e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleEmailSearch(userIndex);
                                        }, error: !!currentUser.emailValidationError, helperText: currentUser.emailValidationError, slotProps: {
                                            input: {
                                                endAdornment: getEmailEndAdornment(currentUser, userIndex, updateUserField, clearEmailValidation)
                                            },
                                            inputLabel: {
                                                sx: {
                                                    '& .MuiInputLabel-asterisk': {
                                                        color: '#d32f2f'
                                                    }
                                                }
                                            }
                                        } }) }), _jsxs(Box, { sx: { display: 'flex', gap: 2, mb: 2 }, children: [_jsx(TextField, { fullWidth: true, size: "small", label: constants.add_user_modal_first_name, placeholder: constants.add_user_modal_first_name, required: true, disabled: currentUser.namesFromAPI, value: currentUser.firstName, onChange: (e) => updateUserField(userIndex, 'firstName', e.target.value), InputLabelProps: {
                                                sx: {
                                                    '& .MuiInputLabel-asterisk': {
                                                        color: '#d32f2f'
                                                    }
                                                }
                                            } }), _jsx(TextField, { fullWidth: true, size: "small", label: constants.add_user_modal_last_name, placeholder: constants.add_user_modal_last_name, required: true, disabled: currentUser.namesFromAPI, value: currentUser.lastName, onChange: (e) => updateUserField(userIndex, 'lastName', e.target.value), InputLabelProps: {
                                                sx: {
                                                    '& .MuiInputLabel-asterisk': {
                                                        color: '#d32f2f'
                                                    }
                                                }
                                            } })] }), _jsx(PhoneInput, { value: currentUser.phoneNumber, country: currentUser.selectedCountry, label: constants.add_user_modal_phone_number, error: currentUser.phoneNumberError, disabled: false, onValueChange: (newValue) => handlePhoneValueChange(newValue, userIndex), onCountryChange: (newCountry) => handleCountryChange(newCountry, userIndex) })] }, currentUser.id))), _jsxs(Box, { onClick: addUser, sx: {
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mt: 2,
                                cursor: 'pointer'
                            }, children: [_jsx(Box, { sx: {
                                        width: 20,
                                        height: 20,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }, children: _jsx("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M10 3.33334V16.6667M3.33334 10H16.6667", stroke: "#26890D", strokeWidth: "1.66667", strokeLinecap: "round", strokeLinejoin: "round" }) }) }), _jsx(Typography, { sx: {
                                        fontFamily: 'Open Sans',
                                        fontSize: 14,
                                        color: '#26890d',
                                        fontWeight: 600
                                    }, children: constants.add_user_modal_add_another_member })] })] }) }), _jsxs(DialogActions, { sx: {
                    p: '24px !important',
                    gap: 2,
                    borderTop: '1px solid #ebebeb'
                }, children: [_jsx(Button, { fullWidth: true, variant: "outlined", onClick: onClose, disabled: loading, sx: {
                            borderColor: '#26890d',
                            color: '#26890d',
                            textTransform: 'none',
                            fontFamily: 'Open Sans',
                            fontWeight: 600,
                            fontSize: 16,
                            px: 2,
                            py: 1,
                            '&:hover': {
                                borderColor: '#26890d',
                                backgroundColor: 'rgba(38, 137, 13, 0.04)'
                            }
                        }, children: constants.cancel }), _jsx(Button, { fullWidth: true, onClick: handleSave, variant: "contained", disabled: loading || !canAdd, sx: {
                            backgroundColor: canAdd ? '#26890d' : '#cccccc',
                            color: '#ffffff',
                            textTransform: 'none',
                            fontFamily: 'Open Sans',
                            fontWeight: 600,
                            fontSize: 16,
                            px: 3,
                            py: 1,
                            '&:hover': {
                                backgroundColor: canAdd ? '#1f6b0a' : '#cccccc'
                            },
                            '&:disabled': {
                                backgroundColor: '#cccccc',
                                color: '#ffffff'
                            }
                        }, children: getButtonText() })] })] }));
};
export default AddUserModal;
